import { Component, DestroyRef, inject, type OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JsonApiClientService, ResourceTypes } from '@big-direkt/json-api-client';
import { UserService } from '@big-direkt/state/user';
import { IconBigMediumBenutzer, IconBigMediumChatbot, IconBigMediumSchliessen, IconBigMediumWinkelUnten, IconComponent } from '@big-direkt/utils/icons';
import { PlatformService } from '@big-direkt/utils/shared';
import { WA_LOCAL_STORAGE } from '@ng-web-apis/common';
import { type Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { UiButtonComponent } from '@big-direkt/ui/button';
import { BotComponent } from './bot/bot.component';
import { LiveComponent } from './live/live.component';
import { ChatService } from './services/chat.service';
import { ViewMode } from './enums/view-mode';
import { Config } from './interfaces/config';

@Component({
    selector: 'big-chat',
    templateUrl: './chat.component.html',
    styleUrl: './chat.component.scss',
    imports: [CdkDrag, IconComponent, UiButtonComponent, BotComponent, LiveComponent],
})
export class ChatComponent implements OnInit {
    private readonly apiClientService = inject(JsonApiClientService);
    private readonly platformService = inject(PlatformService);
    private readonly userService = inject(UserService);
    private readonly localStorage = inject(WA_LOCAL_STORAGE);
    private readonly destroyRef = inject(DestroyRef);

    public readonly iconUser = IconBigMediumBenutzer;
    public readonly iconChatBot = IconBigMediumChatbot;
    public readonly iconClose = IconBigMediumSchliessen;
    public readonly iconAngleBottom = IconBigMediumWinkelUnten;
    public readonly chatService = inject(ChatService);
    public readonly config = this.chatService.config;
    public readonly viewModeEnum = ViewMode;

    public ngOnInit(): void {
        if (this.platformService.isPlatformServer()) {
            return;
        }

        this.apiClientService
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .load<Config>('', ResourceTypes.ChatConfiguration, { 'ngsw-bypass': 'true' })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(
                    (configWithoutUser): Observable<Config> =>
                        this.userService.getProfile().pipe(
                            switchMap(profile => {
                                if (!profile) {
                                    return of({
                                        ...configWithoutUser,
                                        user: {},
                                    });
                                }

                                return of({
                                    ...configWithoutUser,
                                    user: {
                                        hin: profile.identifier.healthInsuranceNumber,
                                        name: `${profile.name.fullTitle ?? ''} ${profile.name.surname ?? ''}`,
                                    },
                                });
                            }),
                        ),
                ),
            )
            .subscribe(config => {
                const storageKey = 'BIG_CHATBOT_TOKEN';

                let token: string | null = this.localStorage.getItem(storageKey);

                if (!token) {
                    token = uuidv4();

                    this.localStorage.setItem(storageKey, token);
                }

                config.user.token = token;
                this.config.set(config);
            });
    }

    public toggleMinimize(): void {
        this.chatService.toggleMinimized();
    }

    public close(): void {
        this.chatService.setVisibility(false);
        this.chatService.setMinimized(false);
        this.chatService.setViewMode(ViewMode.chooseChat);
    }

    public setViewMode(value: ViewMode): void {
        this.chatService.setViewMode(value);
    }

    public onChatToggleHandler(viewMode: ViewMode = ViewMode.chooseChat): void {
        if (this.config().panel.viewMode !== viewMode && viewMode !== ViewMode.chooseChat) {
            this.chatService.setVisibility(true);
        } else {
            this.chatService.toggleVisibility();
        }

        this.chatService.setViewMode(viewMode);
    }

    public onHandover(handover: boolean): void {
        if (handover) {
            this.chatService.setViewMode(ViewMode.liveChat);
        }
    }
}
